/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-underscore-dangle */
import React, { useState, useRef, useEffect } from 'react';
import { format, startOfYear, addDays, addMonths, addYears, isSameMonth } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { Tooltip } from 'react-tooltip';
import { createPortal } from 'react-dom';

import './DateRangePicker.scss';

import CalendarIcon from '../../assets/icons/calendar-btn-icon.svg';
import InfoIcon from '../../assets/icons/info-icon-black.svg';
import DownArrowIcon from '../../assets/icons/down-arrow.svg';
import CloseIcon from '../../assets/icons/cross-circle.svg';

interface DateRange {
  from?: Date | null;
  to?: Date | null;
  __label?: string;
}

interface QuickRangeOption {
  label: string;
  calculateRange?: () => { from?: Date; to?: Date };
  onSelect?: (onChange: CustomDateRangePickerProps['onChange']) => void;
  keepOpen?: boolean;
}

interface CustomDateRangePickerProps {
  onChange?: (range: { startDate: Date | null; endDate: Date | null }) => void;
  value?: { startDate: Date | null; endDate: Date | null };
  quickRanges?: QuickRangeOption[];
  placeholder?: string;
  formatSelected?: (range: DateRange | undefined) => string;
  hideInfoIcon?: boolean;
  usePortal?: boolean;
  autoOpen?: boolean;
  centerOnScreen?: boolean;
  hideTrigger?: boolean;
  onCancel?: () => void;
  disableFutureDates?: boolean;
  className?: string;
  FutureDefaultDays?: boolean
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  onChange,
  value,
  quickRanges,
  placeholder = 'Select Date Range',
  formatSelected,
  hideInfoIcon = false,
  usePortal = false,
  autoOpen = false,
  centerOnScreen = false,
  hideTrigger = false,
  onCancel,
  disableFutureDates = false,
  className = '',
  FutureDefaultDays = false
}) => {
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value?.startDate === null && value?.endDate === null) {
      setRange(undefined);
    } else if (value?.startDate || value?.endDate) {
      setRange({
        from: value.startDate ?? undefined,
        to: value.endDate ?? undefined,
      });
    }
  }, [value]);

  const openPanel = () => {
    setDraftRange(range);
    setIsOpen(true);
  };

  const closePanel = () => {
    setDraftRange(range);
    setIsOpen(false);
    onCancel?.();
  };

  useEffect(() => {
    if (autoOpen) {
      const initialRange = value?.startDate || value?.endDate
        ? { from: value.startDate ?? undefined, to: value.endDate ?? undefined }
        : undefined;
      setDraftRange(initialRange);
      setIsOpen(true);
    }
  }, []);

  const handleSelect = (selectedRange: any | undefined) => {
    setDraftRange(selectedRange);
  };

  const applyRange = () => {
    setRange(draftRange);
    onChange?.({
      startDate: draftRange?.from || null,
      endDate: draftRange?.to || null,
    });
    setIsOpen(false);
  };

  const resetRange = () => {
    setRange(undefined);
    setDraftRange(undefined);
    onChange?.({ startDate: null, endDate: null });
  };

  const defaultQuickRanges: QuickRangeOption[] = [
    ...(FutureDefaultDays
      ? [
        {
          label: 'Next 7 days',
          calculateRange: () => {
            const start = new Date();
            const end = addDays(start, 6);
            return { from: start, to: end };
          },
        },
        {
          label: 'Next 30 days',
          calculateRange: () => {
            const start = new Date();
            const end = addDays(start, 29);
            return { from: start, to: end };
          },
        },
        {
          label: 'Next 60 days',
          calculateRange: () => {
            const start = new Date();
            const end = addDays(start, 59);
            return { from: start, to: end };
          },
        },
        {
          label: 'Next 90 days',
          calculateRange: () => {
            const start = new Date();
            const end = addDays(start, 89);
            return { from: start, to: end };
          },
        },
      ] 
      : [
        {
          label: 'Past 7 days',
          calculateRange: () => {
            const end = new Date();
            const start = addDays(end, -6);
            return { from: start, to: end };
          },
        },
        {
          label: 'Past 30 days',
          calculateRange: () => {
            const end = new Date();
            const start = addDays(end, -29);
            return { from: start, to: end };
          },
        },
        {
          label: 'Past 60 days',
          calculateRange: () => {
            const end = new Date();
            const start = addDays(end, -59);
            return { from: start, to: end };
          },
        },
        {
          label: 'Past 90 days',
          calculateRange: () => {
            const end = new Date();
            const start = addDays(end, -89);
            return { from: start, to: end };
          },
        },
      ]),
    {
      label: 'YTD',
      calculateRange: () => {
        const end = new Date();
        const start = startOfYear(new Date());
        return { from: start, to: end };
      },
    },
    {
      label: 'Select Date Range',
      keepOpen: true,
      onSelect: () => {
        setDraftRange(undefined);
      },
    },
  ];

  const rangesToUse = quickRanges || defaultQuickRanges;

  const formattedRange = range?.__label
    ? range.__label
    : range?.from || range?.to
      ? formatSelected
        ? formatSelected(range)
        : range?.from && range?.to
          ? `${format(range.from, 'MMM dd, yyyy')} - ${format(range.to, 'MMM dd, yyyy')}`
          : range?.from
            ? `From ${format(range.from, 'MMM dd, yyyy')}`
            : range?.to
              ? `Until ${format(range.to, 'MMM dd, yyyy')}`
              : placeholder
      : placeholder;

  const PANEL_WIDTH = 875;
  const VIEWPORT_MARGIN = 16;

  const getPortalLeft = () => {
    const rect = dropdownRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;
    return Math.min(rect.left, Math.max(VIEWPORT_MARGIN, maxLeft));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!usePortal && !centerOnScreen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      closePanel();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [range]);

  const navStartMonth = addYears(new Date(), -10);
  const navEndMonth = disableFutureDates ? new Date() : addYears(new Date(), 10);
  const disabledDays = disableFutureDates ? { after: new Date() } : undefined;

  const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();

  let leftDefaultMonth = range?.from ?? new Date();
  let rightDefaultMonth = range?.to && !isSameMonth(range.to, leftDefaultMonth)
    ? range.to
    : addMonths(leftDefaultMonth, 1);

  if (monthIndex(rightDefaultMonth) > monthIndex(navEndMonth)) {
    rightDefaultMonth = navEndMonth;
    leftDefaultMonth = addMonths(navEndMonth, -1);
  }

  const [leftMonth, setLeftMonth] = useState<Date>(leftDefaultMonth);
  const [rightMonth, setRightMonth] = useState<Date>(rightDefaultMonth);

  useEffect(() => {
    if (isOpen) {
      setLeftMonth(leftDefaultMonth);
      setRightMonth(rightDefaultMonth);
    }
  }, [isOpen]);

  const handleLeftMonthChange = (month: Date) => {
    setLeftMonth(month);
    if (monthIndex(month) >= monthIndex(rightMonth)) {
      setRightMonth(addMonths(month, 1));
    }
  };

  const handleRightMonthChange = (month: Date) => {
    setRightMonth(month);
    if (monthIndex(month) <= monthIndex(leftMonth)) {
      setLeftMonth(addMonths(month, -1));
    }
  };

  const dropdownContent = (
    <div className="dropdown-panel">
      {/* {usePortal && (
        <div style={{ position: 'absolute', top: -19, right: 3, cursor: 'pointer' }} onClick={closePanel}>
          <img src={CloseIcon} alt="close" />
        </div>
      )} */}

      <div className="dropdown-body">
        <div className="quick-ranges">
          <div>
            {rangesToUse.map((r) => (
              <div
                key={r.label}
                className="range-option"
                onClick={() => {
                  if (r.onSelect) {
                    r.onSelect(onChange);
                    if (!r.keepOpen) {
                      setRange({ from: new Date(), to: null, __label: r.label } as any);
                    }
                  } else if (r.calculateRange) {
                    const { from, to } = r.calculateRange();
                    setRange({ from, to });
                    onChange?.({ startDate: from ?? null, endDate: to ?? null });
                  }
                  if (!r.keepOpen) {
                    setIsOpen(false);
                  }
                }}
              >
                {r.label}
              </div>
            ))}
          </div>
          <div className="reset" onClick={resetRange}>Reset</div>
        </div>

        <div className="calendar-panel">
          <div className="calendar-container">
            <div className="calendar">
              <DayPicker
                mode="range"
                selected={draftRange}
                onSelect={handleSelect}
                numberOfMonths={1}
                month={leftMonth}
                onMonthChange={handleLeftMonthChange}
                startMonth={navStartMonth}
                endMonth={navEndMonth}
                disabled={disabledDays}
                showOutsideDays
                captionLayout="dropdown"
                modifiersClassNames={{
                  selected: 'rdp-day_selected',
                  range_start: 'rdp-day_range_start',
                  range_end: 'rdp-day_range_end',
                  range_middle: 'rdp-day_range_middle',
                }}
              />
            </div>
            <div className="calendar">
              <DayPicker
                mode="range"
                selected={draftRange}
                onSelect={handleSelect}
                numberOfMonths={1}
                month={rightMonth}
                onMonthChange={handleRightMonthChange}
                startMonth={navStartMonth}
                endMonth={navEndMonth}
                disabled={disabledDays}
                showOutsideDays
                captionLayout="dropdown"
                modifiersClassNames={{
                  selected: 'rdp-day_selected',
                  range_start: 'rdp-day_range_start',
                  range_end: 'rdp-day_range_end',
                  range_middle: 'rdp-day_range_middle',
                }}
              />
            </div>
          </div>

          <div className="dropdown-footer">
            <button type="button" className="btn-cancel" onClick={closePanel}>Cancel</button>
            <button type="button" className="btn-apply" onClick={applyRange}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`custom-date-range-picker ${className}`.trim()} ref={dropdownRef}>
      {!hideTrigger && (
        <div
          className="dropdown-trigger"
          onClick={() => (isOpen ? closePanel() : openPanel())}
          role="button"
          tabIndex={0}
        >
          <div className="left">
            <img src={CalendarIcon} alt="Calendar Icon" className="calendar-icon" />
            <span className="label">{formattedRange}</span>
            {!hideInfoIcon && (
              <span>
                <img
                  src={InfoIcon}
                  alt="info"
                  className="cursor-pointer"
                  data-tooltip-id="tooltip-date"
                  data-tooltip-content="Current Date is not included when past 30/60/90 days or YTD is selected."
                />
                <Tooltip id="tooltip-date" place="bottom" className="common-tooltip" style={{ width: '180px' }} />
              </span>
            )}
          </div>
          <img src={DownArrowIcon} alt="down Icon" />
        </div>
      )}

      {isOpen && (
        centerOnScreen
          ? createPortal(
            <div
              className="dropdown-portal"
              style={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
              }}
            >
              {dropdownContent}
            </div>,
            document.body
          )
          : usePortal
            ? createPortal(
              <div
                className="dropdown-portal"
                style={{
                  position: 'absolute',
                  top: dropdownRef.current?.getBoundingClientRect().bottom ?? 0,
                  left: getPortalLeft(),
                  zIndex: 2000,
                }}
              >
                {dropdownContent}
              </div>,
              document.body
            )
            : dropdownContent
      )}
    </div>
  );
};

export default CustomDateRangePicker;
